import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4RentersInsuranceArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'What Is HO-4 / Renter\'s Insurance? | GetSkimp Self Help Library';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Learn what HO-4 renters insurance covers, what it does not cover, and how to choose limits, deductibles, and liability for your rental.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="container">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '260px 1fr',
          gap: '2rem',
        }}
      >
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/what-is-ho4-renters-insurance"
          onNavigate={(href) => navigate(href)}
        />

        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={() => navigate('/knowledge-base')}
              style={{ marginBottom: '1rem' }}
            >
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>What is HO-4 / Renter&apos;s Insurance?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              A simple guide to what HO-4 covers, what it does not cover, and how to choose the right policy for your
              apartment.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>
              Last modified: {HO4_LAST_MODIFIED}
            </p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                HO-4 is the official insurance form used for renters insurance. It helps protect your personal
                belongings, can cover temporary living costs after certain losses, and includes{' '}
                <Link to="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance" style={articleLinkStyle}>
                  liability coverage
                </Link>{' '}
                if someone is hurt in your rented space.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What HO-4 usually covers</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Personal property:</strong> Furniture, clothes, electronics, and other belongings in your
                  unit.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Loss of use:</strong> Hotel and related costs if your unit becomes unlivable after a covered
                  claim.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Personal liability:</strong> Coverage if you are responsible for injury or property damage to
                  others.
                </li>
                <li>
                  <strong>Medical payments:</strong> Limited medical coverage for certain guest injuries, regardless of
                  fault.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What HO-4 does not usually cover</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  Damage to the building itself (this is usually the landlord&apos;s policy). See{' '}
                  <Link
                    to="/knowledge-base/how-is-ho4-renters-insurance-different-from-homeowners-insurance"
                    style={articleLinkStyle}
                  >
                    how HO-4 differs from homeowners insurance
                  </Link>
                  .
                </li>
                <li style={{ marginBottom: '0.5rem' }}>Flood damage unless flood coverage is added separately.</li>
                <li>High-value items above policy limits unless scheduled or endorsed.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>How to pick a good policy</h3>
              <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  Estimate how much your belongings would cost to replace today.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Choose a deductible that balances monthly cost and out-of-pocket risk.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Review{' '}
                  <Link to="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance" style={articleLinkStyle}>
                    liability limits
                  </Link>{' '}
                  carefully, especially if roommates or pets are involved.
                </li>
                <li>Ask about optional coverages for valuables and identity theft.</li>
              </ol>
              <p style={{ marginTop: '1rem', marginBottom: 0 }}>
                Ready to buy a policy? Use this guide on{' '}
                <Link to="/knowledge-base/how-to-get-ho4-renters-insurance" style={articleLinkStyle}>
                  how to get HO-4 renters insurance
                </Link>
                .
              </p>
            </div>
          </article>

          <section
            className="card mb-4"
            style={{
              borderColor: 'var(--color-green)',
              background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>
                Ready to move forward? Begin your renters insurance flow in just a few steps.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/qualify')}
                style={{ fontWeight: 700, paddingInline: '1.4rem' }}
              >
                Get started now
              </button>
            </div>
          </section>

          <section
            className="card"
            style={{
              borderColor: '#FBBF24',
              background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)',
            }}
          >
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Prefer to speak to an agent?</h3>
              <p style={{ marginBottom: '1rem' }}>
                Get one-on-one guidance before you choose coverage. We&apos;ll walk you through limits, deductibles,
                and options.
              </p>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate('/help')}
                style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}
              >
                Speak to an agent
              </button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4RentersInsuranceArticlePage;
